/**
 * @fileoverview Story list page — loads and renders the story catalog grid.
 */

import { useState, useEffect } from 'preact/hooks';
import { BasePage } from './BasePage.js';
import { StoryService, type StorySummary } from '../../services/StoryService.js';
import { tapProps } from '../../utilities/tapProps.js';
import { storyStrings } from '../../i18n/stories.js';

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function StoryCard({ story, onBuy }: {
    story: StorySummary;
    onBuy: (id: string, price: number) => void;
}) {
    const canRead = story.isFree || story.isPurchased;
    const navigate = (path: string) => (window as any).__appNavigate?.(path);
    const openStory = () => navigate(`/stories/${story.id}`);

    return (
        <div
            class={`story-card${canRead ? ' story-card--readable' : ''}`}
            {...(canRead ? { onClick: openStory } : {})}
            style={canRead ? { cursor: 'pointer' } : undefined}
        >
            <img src={story.thumbnail} alt={story.title} class="story-thumbnail" />
            <h3>{story.title}</h3>
            <span class="story-age">{storyStrings.age} {story.age}+</span>
            <span class="story-pages">{story.pageCount} {storyStrings.pages}</span>

            {canRead ? (
                <span class="purchased-badge">{storyStrings.readBadge}</span>
            ) : (
                <div class="story-price-container">
                    <span class="story-price">${story.price.toFixed(2)}</span>
                    <button class="buy-story-btn" {...tapProps(() => onBuy(story.id, story.price))}>
                        {storyStrings.buyNow}
                    </button>
                </div>
            )}
        </div>
    );
}

function StoryListView() {
    const [stories, setStories] = useState<StorySummary[]>([]);
    const navigate = (path: string) => (window as any).__appNavigate?.(path);

    useEffect(() => {
        StoryService.loadCatalog().then(setStories);
    }, []);

    const handleBuy = async (storyId: string, price: number) => {
        const purchased = await StoryService.initiatePurchase(storyId, price);
        if (purchased) {
            const updated = await StoryService.loadCatalog();
            setStories(updated);
        }
    };

    return (
        <div class="page-inner">
            <div class="page-header">
                <button class="page-back-icon" {...tapProps(() => navigate('/'))}>
                    <span class="material-icons">arrow_back</span>
                </button>
                <h1>{storyStrings.pageTitle}</h1>
            </div>
            <div class="story-grid">
                {stories.map(story => (
                    <StoryCard key={story.id} story={story} onBuy={handleBuy} />
                ))}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page class
// ---------------------------------------------------------------------------

export class StoryListPage extends BasePage {
    constructor() {
        super('storyListPage');
    }

    protected override async render(): Promise<void> {
        this.renderPreact(<StoryListView />);
    }
}
